import CubemapUtil from '../helpers/cubemap.util';
import Pannellum from "../pannellum";
import {FONT_SIZE} from "a_root-container/react-planner/src/constants";

const clone = require('lodash/clone');
const cloneDeep = require('lodash/cloneDeep');
const pick = require('lodash/pick');
const shortid = require('shortid');


// todo: for now we keep 2 different floorplans versions. Need some more solid way to keep data in one place.
// For now the config divide on <view> (actually what we see in pannelum) and <planner> (what we draw in react-planner)
// floorplan:{
//  e3view: {},
//  e3planner: {}
// }

/**
 * Service for organizing floorplan data flow.
 *
 * @typedef {Object} AreaData
 * @property {boolean} generated
 * @property {string} scene
 * @property {number} tripodHeight
 * @property {number[]} camera
 * @property {number} direction
 * @property {Object.<string, number[]>} points
 * @property {Object[]} holes
 * @property {number} holes[].angle
 * @property {number[]} holes[].coord
 * @property {string[]} holes[].scenes
 *
 */

export default class FloorplanManager {
    /**
     * @type {number[][]} snapAngles
     * {number} snapAngles[][0] - resulted angle
     * {number} snapAngles[][1] - angle start
     * {number} snapAngles[][2] - angle end
     */
    static snapAngles = [
        [90, 0, 110],
        [120, 110, 130],
        [135, 130, 140],
        [150, 140, 160],
        [180, 160, 180],
    ].map(a => a.map(b => b * Pannellum.RADIAN));
    static padding = 500;
    static thickness = 200;
    static defaults = {
        height_lens: {name: 'Default', height: 1700},
        ceiling: 2400,
    };
    _areaId = null;
    _layerId = null;
    _sceneId = null;
    _sceneName = null;
    _vertices = [];
    _hotspots = null;
    _snapMode = true;
    _pitchRatio = 0;
    _floorplan = null;
    _floorplanPreview = null;
    _wallHeight = 3000;

    /**
     * @param {Object} floorplan
     * @param {Object} options
     * @param {boolean} options.fromEditor
     * @param {Object[]} options.scenes
     * @param {string} options.scenes[].id
     * @param {string} options.scenes[].name
     */
    constructor(floorplan, options, title) {
        ({floorplan: this._floorplan, floorplanPreview: this._floorplanPreview, changePreview: this._changePreview} = this._prepareFloorplan(floorplan, options, title));
    }

    static sphereTo2dCorrected(yaw, pitch, {poseRoll = 0, posePitch = 0}) {
        ([yaw, pitch] = this.sphereCoordsCorrection(yaw, pitch, poseRoll, posePitch));

        let dist = pitch < 0 ? Math.tan(pitch + Math.PI / 2) : Math.tan(Math.PI / 2 - pitch) ;
        let angle = -yaw;

        return [dist * Math.cos(angle), dist * Math.sin(angle)];
    }

    static sphereCoordsCorrection(yaw, pitch, poseRoll = 0, posePitch = 0) {
        let [x,y,z] = this.spher2Cart(yaw, pitch, poseRoll, posePitch);

        let r = Math.sqrt(x*x + y*y + z*z);

        pitch = Math.asin(Math.max(Math.min(z/r, 1), -1));
        yaw = Math.atan2(x, y);

        return [yaw, pitch];
    }

    static xToYaw(x) {
        return x * 360 - 180;
    }

    static yToPitch(y) {
        return (y - 0.5) * -180;
    }

    // todo: deal with 2d coords
    static sphereTo2dNormalized(yaw, pitch) {
        let dist = pitch < 0 ? Math.tan(pitch + Math.PI / 2) : Math.tan(Math.PI / 2 - pitch) ;
        let angle = -yaw;

        return [dist * Math.cos(angle), dist * Math.sin(angle)];
    }

    // todo: deal with 2d coords
    static sphere22d(yaw, pitch, {poseRoll = 0, posePitch = 0}) {
        ([yaw, pitch] = this.sphereCoordsCorrection(yaw, pitch, poseRoll, posePitch));

        let dist = pitch < 0 ? Math.tan(pitch + Math.PI / 2) : Math.tan(Math.PI / 2 - pitch) ;
        let angle = -yaw + Math.PI / 2;

        return [dist * Math.cos(angle), dist * Math.sin(angle)];
    }

    static normalizedSphereTo2d (yaw, pitch) {
        let dist = pitch < 0 ? Math.tan(pitch + Math.PI / 2) : Math.tan(Math.PI / 2 - pitch);
        let angle = yaw;

        return [dist * Math.cos(angle), dist * Math.sin(angle)];
    }

    static cross (l1, l2) {
        let a1; let b1;
        let a2; let b2;

        a1 = (l1[1].y- l1[0].y) / (l1[1].x - l1[0].x);
        b1 = l1[0].y - a1 * l1[0].x;

        a2 = (l2[1].y - l2[0].y) / (l2[1].x - l2[0].x);
        b2 = l2[0].y - a2 * l2[0].x;

        let x = (b2 - b1) / (a1 - a2);
        let y = x * a1 + b1;

        return [x, y];
    }

    static distanceBetweenPoints(p1, p2) {
        return Math.sqrt((p2.y - p1.y) * (p2.y - p1.y) + (p2.x - p1.x) * (p2.x - p1.x));
    }


    /**
     * Convert from [yaw, pitch] to [x,y,z] applying poseRoll and posePitch correction if available
     * Convert to RIGHT-hand coordinate system
     * Rotate rules according to https://developers.google.com/streetview/spherical-metadata
     * @param {number} yaw
     * @param {number} pitch
     * @param {number} poseRoll
     * @param {number} posePitch
     * @returns {number[]} [x,y,z]
     */
    static spher2Cart(yaw, pitch, poseRoll = 0, posePitch = 0){
        let x = Math.cos(pitch)*Math.sin(yaw),
            y = Math.cos(pitch)*Math.cos(yaw),
            z = Math.sin(pitch);

        if (poseRoll || posePitch) {
            let ox = x * Math.cos(poseRoll) + Math.sin(poseRoll) * ( y * Math.sin(posePitch) + z * Math.cos(posePitch) );
            let oy = y * Math.cos(posePitch) - z * Math.sin(posePitch);
            z = -x * Math.sin(poseRoll) + Math.cos(poseRoll) * ( y * Math.sin(posePitch) + z * Math.cos(posePitch) );
            x = ox; y=oy;
        }

        return [x,y,z];
    }

    /**
     * Convert from [x,y,z] to [yaw, pitch] applying poseRoll and posePitch correction if available
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @param {number} poseRoll
     * @param {number} posePitch
     * @returns {[number, number]} [yaw, pitch]
     */
    static cart2Spher(x,y,z, poseRoll = 0, posePitch = 0) {
        if (poseRoll || posePitch) {
            poseRoll = -poseRoll;
            posePitch = -posePitch;

            let ox = x * Math.cos(poseRoll) + z * Math.sin(poseRoll);
            let oy = y * Math.cos(posePitch) + Math.sin(posePitch) * ( x * Math.sin(poseRoll) - z * Math.cos(poseRoll) );
            z = y * Math.sin(posePitch) - Math.cos(posePitch) * ( x * Math.sin(poseRoll) - z * Math.cos(poseRoll) );
            x = ox; y=oy;
        }

        let r = Math.sqrt(x*x + y*y + z*z);
        let pitch = Math.asin(Math.max(Math.min(z/r, 1), -1));
        let yaw = Math.atan2(x, y);

        return [yaw, pitch]
    }

    /**
     * Get center point between 2 spherical points
     * @param {number[]} p1
     * @param {number[]} p2
     * @param {number} poseRoll
     * @param {number} posePitch
     * @returns {(number)[]}
     */
    static getSpherCenter(p1, p2, poseRoll = 0, posePitch = 0) {
        let planar = [];

        let pair = [p1,p2].sort((a,b) => a[0] - b[0]);

        pair.forEach((p, i) => {
            planar[i] = FloorplanManager.spher2Cart(...p, poseRoll, posePitch);
        });

        let {x,y,z} = planar.reduce((p1,p2) => ({
            x: (p1[0] + p2[0]) / 2,
            y: (p1[1] + p2[1]) / 2,
            z: (p1[2] + p2[2]) / 2
        }));

        return FloorplanManager.cart2Spher(x,y,z, poseRoll, posePitch);
    }

    /**
     * Get angle between 2D line and OX axis
     * @param {number} x1
     * @param {number} y1
     * @param {number} x2
     * @param {number} y2
     * @returns {number}
     */
    static get2DLineAngle([x1, y1], [x2, y2]) {
        return Math.atan2(y2 - y1, x2 - x1);
    }

    /**
     * Normalize yaw value within the range from -Math.PI to Math.PI
     * @param {number} yaw
     * @returns {number}
     */
    static normalizeYaw(yaw) {
        let PI2 = Math.PI * 2;
        return ((yaw + PI2 + Math.PI) % PI2) - Math.PI;
    }

    /**
     * converts 2D point to [yaw, pitch]
     * @param {number} x
     * @param {number} y
     * @returns {number[]}
     */
    static point2DToSpher(x, y) {
        let yaw = -Math.atan2(y, x);
        let dist = Math.sqrt(x * x + y * y);
        let pitch = -Math.PI / 2 + Math.atan(dist);

        return [yaw, pitch];
    }

    /**
     * Rotate 2d point related to (0,0)
     * @param {number} x
     * @param {number} y
     * @param {number} angle
     * @returns {number[]}
     */
    static rotate2DPoint([x, y], angle) {
        let dist = Math.sqrt(x * x + y * y);
        angle += Math.atan2(y, x);
        return [Math.cos(angle) * dist, Math.sin(angle) * dist];
    }

    /**
     * Rotate each point of area
     * @param {object} area
     * @param {number} angle
     * @returns {object}
     */
    static rotate2DArea(area, angle) {
        area.points = FloorplanManager.rotate2DPoints(area.points, angle);
        area.holes.forEach(hole => {
            hole.coord = FloorplanManager.rotate2DPoint(hole.coord, angle);
            hole.angle += angle;
        });
        area.camera = FloorplanManager.rotate2DPoint(area.camera, angle);
        return area;
    }

    /**
     * Moave each point of area
     * @param {object} area
     * @param {number} angle
     * @returns {object}
     */
    static move2DArea(area, distance) {
        area.points = FloorplanManager.move2DPoints(area.points, distance);
        // area.holes.forEach(hole => {
        //     hole.coord = FloorplanManager.move2DPoints(hole.coord, distance);
        // });
        // area.camera = FloorplanManager.move2DPoints(area.camera, distance);
        return area;
    }

    static move2DPoints(points, distance) {
        if (Array.isArray(points))
            return points.map(c => c + distance);

        let res = {};
        for (let i in points) {
            if (points.hasOwnProperty(i)) {
                res[i] = points[i].map(c => FloorplanManager.move2DPoint(c,  distance));
            }
        }

        return res;
    }

    static move2DPoint([x, y], [xOffset, yOffset]) {
        return [x + xOffset, y + yOffset];
    }
    /**
     * Rotate each vertex of vertices Map
     * @param {Object.<string, [number, number]>} points
     * @param {number} angle
     */
    static rotate2DPoints(points, angle) {
        let res = {};
        for (let i in points) {
            if (points.hasOwnProperty(i))
                res[i] = FloorplanManager.rotate2DPoint(points[i], angle);
        }
        return res;
    }

    /**
     * Scale coords of 2D points
     * @param {number[] || object}points
     * @param {number} ratio
     * @returns {*}
     */
    static scale2DPoints(points, ratio) {
        if (Array.isArray(points))
            return points.map(c => c * ratio);

        let res = {};
        for (let i in points) {
            if (points.hasOwnProperty(i)) {
                res[i] = points[i].map(c => c * ratio);
            }
        }

        return res;
    }

    /**
     * Scale each points, holes coords of area
     * @param {object} area
     * @param {number} ratio
     * @returns {object}
     */
    static scale2DArea(area, ratio) {
        area.points = FloorplanManager.scale2DPoints(area.points, ratio);
        area.holes.forEach(hole => {
            hole.coord = FloorplanManager.scale2DPoints(hole.coord, ratio);
        });
        area.camera = FloorplanManager.scale2DPoints(area.camera, ratio);

        return area;
    }
    /**
     * Find angle between 2 lines on plane
     * @param {object} line1
     * @param {object} line2
     * @param {object} intersection
     * @returns {number}
     */
    static angleBetween2DLines(line1, line2, intersection) {
        const line1ToIntersect = Math.sqrt(Math.pow(intersection.x - line1.x, 2) + Math.pow(intersection.y - line1.y, 2)); // p0->c (b)
        const line2ToIntersect = Math.sqrt(Math.pow(intersection.x - line2.x, 2) + Math.pow(intersection.y - line2.y, 2)); // p1->c (a)
        const line1ToLine2 = Math.sqrt(Math.pow(line2.x - line1.x, 2) + Math.pow(line2.y - line1.y, 2)); // p0->p1 (c)

        if(isNaN( Math.acos((line2ToIntersect *line2ToIntersect + line1ToIntersect * line1ToIntersect - line1ToLine2 * line1ToLine2) / (2 * line2ToIntersect * line1ToIntersect)))){
            debugger
        }
        return Math.acos((line2ToIntersect *line2ToIntersect + line1ToIntersect * line1ToIntersect - line1ToLine2 * line1ToLine2) / (2 * line2ToIntersect * line1ToIntersect));
    }
    /**
     * Find coords of point which is lie on perpendicular line to
     * (start,end) line and on <distance> from theirs intersection point
     * @param {object} start
     * @param {object} end
     * @param {number} distance
     * @returns {number[]} coordinates of perpendicular point
     */
    static perpendicular2DLine(start, end, distance){
        let x = start.x - end.x;
        let y = start.y - end.y;
        const length = Math.sqrt((x*x + y*y));
        const cosX  = x / length;
        const cosY  = y / length;

        let dx  = (cosY > 0 ?  cosY :  -cosY) * distance;
        let dy  = (cosX > 0 ?  cosX : -cosX) * distance;

        x = start.x + dx;
        y = start.y + dy;

        let angle = FloorplanManager.angleBetween2DLines(end, {x,y}, start) / Pannellum.RADIAN;

        if (Number(angle.toFixed()) !== 90) {
            dx  = (cosY > 0 ?  -cosY :  cosY) * distance;
            dy  = (cosX > 0 ?  cosX : -cosX) * distance;

            x = start.x + dx;
            y = start.y + dy;
        }

        return [x, y]
    }

    /**
     * Move 2D point on plane
     * @param {number} x
     * @param {number} y
     * @param {number} xOffset
     * @param {number} yOffset
     * @returns {*[]}
     */
    static translate2DPoint([x, y], [xOffset, yOffset]) {
        return [x + xOffset, y + yOffset];
    }

    /**
     * Move each point and hole of area on plane
     * @param {object} area
     * @param {number[]} translation
     * @returns {object}
     */
    static translate2DArea(area, translation) {
        area.points = FloorplanManager.translate2DPoints(area.points, translation);
        area.holes.forEach(hole => {
            hole.coord = FloorplanManager.translate2DPoint(hole.coord, translation);
        });
        area.camera = FloorplanManager.translate2DPoint(area.camera, translation);
        return area;
    }

    /**
     * Move 2D points on plane
     * @param {Object.<string, [number, number]>} points
     * @param {number[]} translation
     * @param {number} translation[0] - x
     * @param {number} translation[1] - y
     */
    static translate2DPoints(points, translation) {
        let res = {};
        for (let i in points) {
            if(points.hasOwnProperty(i)) {
                res[i] = FloorplanManager.translate2DPoint(points[i], translation);
            }
        }
        return res;
    }

    /**
     * Move (0,0) point to left top angle of 2D plane
     * @param {object} floorplan
     * @returns {object}
     */
    static normalizeData(floorplan) {
        let gminX = Number.POSITIVE_INFINITY, gminY = Number.POSITIVE_INFINITY;
        let gmaxX = Number.NEGATIVE_INFINITY, gmaxY = Number.NEGATIVE_INFINITY;

        for (let l in floorplan.layers) {
            let layer = floorplan.layers[l];

            let minX = Number.POSITIVE_INFINITY, minY = Number.POSITIVE_INFINITY;
            let maxX = Number.NEGATIVE_INFINITY, maxY = Number.NEGATIVE_INFINITY;

            for (let v in layer.vertices) {
                let vres = layer.vertices[v];
                minX = Math.min(minX, vres.x);
                minY = Math.min(minY, vres.y);
                maxX = Math.max(maxX, vres.x);
                maxY = Math.max(maxY, vres.y);
            }
            for (let v in layer.vertices) {
                let vertex = layer.vertices[v];

                vertex.x = Math.round(vertex.x - minX + FloorplanManager.padding); //padding = left border
                vertex.y = Math.round(vertex.y - minY + FloorplanManager.padding); //padding top border
            }
            for (let a in layer.areas) {
                let cam = layer.areas[a].properties.scene.camera;
                cam.x = Math.round(cam.x - minX + FloorplanManager.padding);
                cam.y = Math.round(cam.y - minY + FloorplanManager.padding);
            }

            gminX = Math.min(gminX, minX);
            gminY = Math.min(gminY, minY);
            gmaxX = Math.max(gmaxX, maxX);
            gmaxY = Math.max(gmaxY, maxY);
        }

        floorplan.width = Math.ceil((gmaxX - gminX) + FloorplanManager.padding * 2) || 0; //padding = right border
        floorplan.height = Math.ceil((gmaxY - gminY) + FloorplanManager.padding * 2) || 0; //padding = bottom border
        floorplan.width  = Number.isFinite(floorplan.width) ? floorplan.width : 4000;
        floorplan.height  = Number.isFinite(floorplan.height) ? floorplan.height : 4000;

        return floorplan;
    }

     /**
     * Calculate ratio of hotspot on the wall
     * @param {number} yaw1
     * @param {number} pitch1
     * @param {number} yaw2
     * @param {number} pitch2
     * @param {number} hotspot_yaw
     * @returns {float}
     * @private
     */
     static findHoleOffset(yaw1, pitch1, yaw2, pitch2, hotspot_yaw) {
        let wall = [{
            yaw: yaw1,
            pitch: pitch1,
        }, {
            yaw: yaw2,
            pitch: pitch2
        }];

        let center = {
            x: 0,
            y: 0
        };

        [wall[0].x, wall[0].y] = FloorplanManager.normalizedSphereTo2d(wall[0].yaw, wall[0].pitch);
        [wall[1].x, wall[1].y] = FloorplanManager.normalizedSphereTo2d(wall[1].yaw, wall[1].pitch);

        let [x, y] = FloorplanManager.cross(wall, [center, {
            x: Math.cos(hotspot_yaw),
            y: Math.sin(hotspot_yaw)
        }]);

        let crosspoint = {
            x: x,
            y: y
        };

        let wall_length = FloorplanManager.distanceBetweenPoints(wall[0], wall[1]);
        let offset = FloorplanManager.distanceBetweenPoints(wall[0], crosspoint);

        return offset / wall_length;
    }

    static getHotspotOffset(hsY, y1, y2) {
        return Math.abs(y1 - y2) > Math.PI
            ? y1 > y2
                ? hsY > y1
                    ? (hsY - y1) / (2 * Math.PI - y1 + y2)
                    :  hsY < y2
                        ? (2 * Math.PI - y1 + hsY) / (2 * Math.PI - y1 + y2)
                        : -1
                : hsY < y1
                    ? (y1 - hsY) / (2 * Math.PI + y1 - y2)
                    : (hsY + y1 - y2 + Math.PI) / (2 * Math.PI + y1 - y2)
            : (hsY - y1) / (y2 - y1);
    }

    static removeSceneFromLayer(layer, areaID) {
        let area = layer.areas[areaID];
        let lines = {};
        let holes = {};

        area.vertices.forEach((vertexID) => {
            const vertex = layer.vertices[vertexID];

            vertex.lines.forEach((lineID) => {
                const line = layer.lines[lineID];

                if (lines[lineID]) return;

                line.holes.forEach((holeID) => {
                    if (!holes[holeID]) {
                        holes[holeID] = layer.holes[holeID];

                        delete layer.holes[holeID];
                    }
                });

                lines[lineID] = line;

                delete layer.lines[lineID];
            });

            delete layer.vertices[vertexID];
        });

        delete layer.areas[areaID];

        lines = null;
        holes = null;
        area = null;

        return layer;
    }

    /**
     *
     * Patch for old tour that have lens height
     * @param area
     * @returns {number}
     */

    static getHeightLens(area) {
        return !area.properties.scene.height_lens ?
                    area.properties.scene.tripod ?
                    Number(area.properties.scene.tripod.height) + Number(area.properties.scene.cameraDevice.height) :
                    FloorplanManager.defaults.height_lens.height :
                Number(area.properties.scene.height_lens.height);
    }

    /**
     * Recalculate positions of top/bottom points in spherical coords
     * @param {boolean} isTopPoint - do we recalculate positions of top (ceiling) points
     * @private
     */
    _recalculateVerticesPairs(isTopPoint) {
        let layer = this._floorplan.layers[this._layerId];
        let props = layer.areas[this._areaId].properties.scene;
        this._vertices.forEach(vertex => {
            let pair = [];

            if (isTopPoint) {
                pair = this._getFloorpointPair(...vertex.floorSpher, props);
                vertex.ceilingSpher = pair[1];
                vertex.ceilingCube = CubemapUtil.getBoxCoords(...pair[1]);
            } else {
                pair = this._getFloorpointPair(...vertex.ceilingSpher, props);
                vertex.floorSpher = pair[0];
                vertex.floorCube = CubemapUtil.getBoxCoords(...pair[0]);
            }

            // vertex.coord2d = FloorplanManager.sphereTo2dNormalized(...pair[0], props);
            vertex.coord2d = FloorplanManager.sphereTo2dCorrected(...pair[0], props);
        });
    }

    /**
     * Check order of two points in sequence
     * @param {number[]} c1
     * @param {number[]} c2
     * @returns {boolean}
     * @private
     */
    _checkSequence(c1, c2) {
        return c1[0] === c2[0] ? c2[1] - c1[1] > 0 : c2[0] - c1[0] > 0;
    }

    /**
     * Create new structured instance of area from config object
     * @param {string} sceneID
     * @param {string} sceneName
     * @param {string} areaID
     * @param {object} height_lens
     * @param {number} ceiling
     * @param {number} poseRoll
     * @param {number} posePitch
     * @param {boolean} addToLayer
     * @returns {{id: *, properties: {scene: {id: *, name: *, height_lens: {name: string, height: number}, ceiling: number, poseRoll: number, posePitch: number, rotateDirection: number, camera: {x: number, y: number}}}, vertices: Array, holes: Array}}
     * @private
     */
    _createArea({sceneID,
                    sceneName,
                    areaID,
                    ceiling = FloorplanManager.defaults.ceiling,
                    poseRoll = 0,
                    posePitch = 0,
                    addToLayer = true,
                    angleForRotateCustomUpdate = 0,
                    hotSpots = [],
                    panorama = ''
                }) {
        let area = {
            id: areaID || shortid.generate(),
            hotSpots: this._createHotspots(hotSpots),
            properties: {
                scene: {
                    id: sceneID,
                    name: sceneName,
                    ceiling,
                    poseRoll,
                    posePitch,
                    panorama,
                    angle: 0,
                    angleForRotateCustomUpdate,
                    camera: {x: 0, y: 0}
                }
            },
            vertices: [],
            holes: []
        };
        area.properties.scene.height_lens = this.getObjHeightLens(area);
        if (this._floorplan && addToLayer) {
            this._floorplan.layers[this._layerId].areas[area.id] = area;
        }

        return area;
    }
    _clockwiseDirections() {
        let tempArr = [];
        let i=0;

        this._vertices.forEach(vertex => {
            tempArr.push(vertex.floorSpher[0]);
        });
        tempArr.push(tempArr[0]);
        tempArr.reduce((v1Id, v2Id) => {
            if(v1Id>v2Id){
                i++;
            }else{
                i--;
            }
            return v2Id;
        });

        return i < 0 ? true : false;
    }

    /**
     * Redo current area with new vertices
     * @private
     */
    _createAreaBasedOnVertices() {
        let verticesArr = [];
        let vertices = {};
        let yaws = [];
        if(this._vertices.length > 0 && !this._clockwiseDirections()) this._vertices.reverse();
        this._vertices.forEach(vertex => {
            let id = shortid.generate();
            vertices[id] = {
                areas: [this._areaId],
                lines: [],
                '2d': [],
                id,
                x: vertex.coord2d[0],
                y: vertex.coord2d[1]
            };
            yaws.push(vertex.floorSpher[0]);
            verticesArr.push(id);
        });

        // reordering vertices indexes to be from -180 to 180 by yaw
        // let minYaw = Math.min(...yaws);
        // let maxYaw = Math.max(...yaws);
        // let minYawIndex = yaws.findIndex(y => y === minYaw);
        // let maxYawIndex = yaws.findIndex(y => y === maxYaw);
        // let diff = minYawIndex - maxYawIndex;
        // if(diff === -1 || diff === yaws.length) {
        //     verticesArr.reverse();
        //     this._vertices.reverse();
        //     minYawIndex = yaws.length - minYawIndex - 1;
        // }
        // verticesArr.push(...verticesArr.splice(0, minYawIndex));
        // this._vertices.push(...this._vertices.splice(0, minYawIndex));

        //creating lines between vertices and holes if there are any hotspots there
        let lines = {};
        let holes = {};
        let layer = this._floorplan.layers[this._layerId];
        let area = layer.areas[this._areaId];
        let {ceiling} = area.properties.scene;
        this._wallHeight = ceiling;
        verticesArr.push(verticesArr[0]);
        verticesArr.reduce((v1Id, v2Id, i) => {
            let v1 = this._vertices[i-1];
            let v2 = this._vertices[i % this._vertices.length];
            let line = this._createLine();
            line.vertices = [v1Id, v2Id];
            vertices[v1Id].lines.push(line.id);
            vertices[v2Id].lines.push(line.id);
            if(!this._checkSequence(v1.coord2d, v2.coord2d)) {
                line.vertices.reverse();
            }
            lines[line.id] = line;

            return v2Id;
        });
        verticesArr.pop();

        area.vertices = verticesArr.slice();
        Object.assign(layer.vertices, vertices);
        Object.assign(layer.lines, lines);
        Object.assign(layer.holes, holes);
        this._setHoles();

        this.setAngleForRotateCustomUpdate(this._layerId);
    }

    /**
     * Create new instance of hole from config object
     * @param {string} lineId
     * @param {string} link
     * @param {number} offset
     * @param {string} hotSpotId
     * @param {boolean} addToLayer
     * @returns {{id: *, properties: {width: {length: number}, height: {length: number}, connect: *[], hotSpotId: *}, offset: *, line: *}}
     * @private
     */
    _createHole({
                    lineId,
                    link,
                    offset,
                    hotSpotId,
                    addToLayer = true
                }, areaId = this._areaId, checked) {
        let layer = this._floorplan.layers[this._layerId];
        let area = layer.areas[areaId];
        let pair = [area.properties.scene.id, link];
        let hole = {
            id: shortid.generate(),
            properties: {
                width: {
                    length: 762
                },
                height: {
                    length: 2150
                },
                connect: pair,
                hotSpotId: hotSpotId
            },
            visible: true,
            offset: offset,
            line: lineId
        };
        if(addToLayer) {
            layer.holes[hole.id] = hole;
        }

        let holeConnect = Object.values(layer.holes).filter((hole, ind) => hole.properties.connect[1] == pair[0] && hole.properties.connect[0] == pair[1]);

        if(!holeConnect.length && checked){
            let areaConnectId = Object.values(layer.areas).filter((area, ind) => area.properties.scene.id == link);
            areaConnectId = areaConnectId.length ? areaConnectId[0].id : null;
            if(areaConnectId){
                let hotspots = layer.areas[areaConnectId].hotSpots;

                let hotspot = hotspots.filter((hotspot) => area.properties.scene.id == hotspot.id);
                let ids = layer.areas[areaConnectId].vertices.slice();
                let vertices = this._getVerticesFromArea(this._layerId, areaConnectId, ids);

                this._setHoles(this._layerId, areaConnectId, hotspot, vertices, ids, false);
            }
        }

        return hole;
    }


    updateScene(scenes) {
        let layer = this._floorplan.layers[this._layerId];
        let areas = Object.values(layer.areas);

        Object.values(scenes).forEach((scene, ind) => {
            areas.forEach((area, ind) => {
                if(scene.id == area.properties.scene.id){
                    area.name = scene.name;
                    area.hotSpots = this._createHotspots(scene.hotSpots);
                }
            })
        })
    }

    updateScenePreview(scenes) {
        let layers = Object.values(this._floorplanPreview.layers)
        let update = false;
        layers.forEach((layer) => {
            let layerPreview = this._floorplanPreview.layers[layer.id];
            let areasPreview = Object.values(layerPreview.areas);

            Object.values(scenes).forEach((scene, ind) => {
                areasPreview.forEach((areaPreview, ind) => {
                    if(scene.id == areaPreview.properties.scene.id){
                        if(areaPreview.properties.scene.name != scene.name){
                            areaPreview.properties.scene.name = scene.name;
                            update = true;
                        }
                    }
                })
            })
        })

        return update;
    }

    _createHotspots(hotSpots) {
        let hostpots = [];

        hotSpots.forEach((hotspot, i) => {
            let spherCoords = [hotspot.yaw, hotspot.pitch]
            hostpots.push({
                equiCoord: spherCoords.map(v => v * Pannellum.RADIAN),
                id: hotspot.id,
                link: hotspot.id
            });
        });

        return hostpots;
    }

    /**
     * Create instance of layer from params
     * @param {string} name
     * @param {string} id
     * @param {number} altitude
     * @param {boolean} addToLayer
     * @returns {{id: *, name: *, angle: number, lines: {}, holes: {}, areas: {}, vertices: {}}}
     * @private
     */
    _createLayer(name, id = shortid.generate(), altitude = 0, addToLayer = true) {
        let layer = {
            id,
            name,
            angle: 0,
            fontSize: FONT_SIZE,
            rotateDirection: 0,
            altitude,
            lines: {},
            holes: {},
            areas: {},
            vertices: {},
            mergeAreas: {},
        };
        if (this._floorplan && addToLayer) {
            this._floorplan.layers[layer.id] = layer;
        }
        return layer;
    }

    /**
     * Create instance of line
     * @param {boolean} addToLayer
     * @returns {{id: *, vertices: Array, holes: Array, properties: {height: {length: number}}}}
     * @private
     */
    _createLine() {
        let line = {
            id: shortid.generate(),
            vertices: [],
            holes: [],
            inverted: null,
            properties: {
                visible: true,
                height: {
                    length: this._wallHeight ? this._wallHeight : 3000
                }
            }
        };
        // if(addToLayer) {
        //     this._floorplan.layers[this._layerId].lines[line.id] = line;
        // }
        return line;
    }


    static point2DToSphere(x, y, {poseRoll = 0, posePitch = 0}) {
        let yaw = -Math.atan2(y, x);
        let dist = Math.sqrt(x * x + y * y);
        let pitch = -Math.PI / 2 + Math.atan(dist);

        x = Math.cos(pitch)*Math.sin(yaw);
        y = Math.cos(pitch)*Math.cos(yaw);

        let z = Math.sin(pitch);
        [yaw, pitch]= FloorplanManager.cart2Spher(x,y,z, poseRoll, posePitch);

        return [yaw, pitch];
    }

    _getCartCoords(layer, areaId, points) {
        let area = layer.areas[areaId];
        let height_lens = FloorplanManager.getHeightLens(area);
        let sceneProps = area.properties.scene;
        let equi = {};
        for (let vid in points) {
            let {posePitch, poseRoll} = sceneProps;
            posePitch *= -1;
            poseRoll *= -1;
            let coordsSphere = FloorplanManager.point2DToSphere(...points[vid], {posePitch, poseRoll});

            let pair = this._getFloorpointPair(...coordsSphere, {posePitch, poseRoll});

            let [x, y] = FloorplanManager.sphere22d(...pair[0], {posePitch, poseRoll});
            let [x1, y1] = FloorplanManager.sphere22d(...pair[1], {posePitch, poseRoll});

            let z = -1;
            let z1 = 1;
            // let height = tripodHeight + cameraHeight;
            let height = height_lens;

            equi[vid] = {
                '2d': [
                    {x: x * height, y: y * height, z: z * height},
                    {x: x1 * height, y: y1 * height, z: z1 * height},
                ],
            }
        }
        return equi;
    }

    _getEquiCoords(layer, areaId, points) {
        let area = layer.areas[areaId];
        let equi = {};
        for (let vid in points) {
            // let coordsEqui = FloorplanManager.point2DToSpher(...points[vid], area.properties.scene);
            let coordsEqui = FloorplanManager.point2DToSphere(...points[vid], area.properties.scene);
            let pair = this._getFloorpointPair(...coordsEqui, area.properties.scene);
            equi[vid] = {bottom: pair[0], top: pair[1]};
        }
        return  equi;
    }

    /**
     * Get area vertices coordinates
     * @param {object} layer
     * @param {string} areaId
     * @param {boolean} isInit - get vertices coords related to
     *                          camera position of area or not
     * @private
     */

    _getAreaLocalCoords(layer, areaId, isInit = false) {
        let area = layer.areas[areaId];
        let vertices = {};
        if (areaId === this._areaId && !isInit) {
            this._vertices.forEach((vertex, i) => {
                if (area.vertices[i]) {
                    vertices[area.vertices[i]] = vertex.coord2d.slice();
                }
            });
        }
        else {
            let {x: cameraX, y: cameraY} = area.properties.scene.camera;
            let height_lens = FloorplanManager.getHeightLens(area);

            area.vertices.forEach(vid => {
                let vertex = layer.vertices[vid];
                vertices[vid] = [
                    (vertex.x - cameraX) / (height_lens),
                    (vertex.y - cameraY) / (height_lens)
                ];
            });
            vertices = FloorplanManager.rotate2DPoints(vertices, -area.properties.scene.rotateDirection - (layer.angle || 0) * Pannellum.RADIAN);
        }

        return vertices;
    }

    getObjHeightLens(area) {
        return{
            height: FloorplanManager.getHeightLens(area),
            id: "-1",
            name: "Custom",
        }
    }

    /**
     * Get area 2D objects.
     * @param layer
     * @param areaId
     * @returns {object} - area objects with 2D coords of vertices and holes
     * @private
     */

    updateScenePanorama({src, sceneId}) {
        if(!this._floorplan) return false;

        const {areaId, layerId} = this.getCurrentSceneParams(sceneId);

        let layer = this._floorplan.layers[layerId];
        let layerPreview = this._floorplanPreview.layers[layerId];

        if(!layer || !layerPreview) return false;

        let area = layer.areas[areaId];
        let areaPreview = layerPreview.areas[areaId];
        if(!areaPreview || !area) return false;

        area.properties.scene = Object.assign(area.properties.scene, {panorama: src});
        areaPreview.properties.scene = Object.assign(areaPreview.properties.scene, {panorama: src});

        return true;
    }

    getCurrentSceneParams(sceneId) {
        for (let l in this._floorplan.layers) {
            let layer = this._floorplan.layers[l];
            for (let a in layer.areas) {
                let area = layer.areas[a];
                if (area.properties.scene.id == sceneId) {

                    return {
                        areaId: area.id,
                        layerId: layer.id,
                    }
                }
            }
        }

        return {
            areaId: null,
            layerId: null,
        }
    }

    /**
     * Get area 2D objects.
     * @param layer
     * @param areaId
     * @returns {object} - area objects with 2D coords of vertices and holes
     * @private
     */

    _getAreaLocalData(layer, areaId) {
        let area = layer.areas[areaId];

        let height_lens = FloorplanManager.getHeightLens(area);
        let data = {
            generated: false,
            scene: area.properties.scene.id,
            panorama: area.properties.scene.panorama,
            height_lens: height_lens,
            camera: [0, 0],
            points: {},
            holes: [],
            coordsEqui: [],
            direction: 0,
        };

        data.points = this._getAreaLocalCoords(layer, areaId);

        data.holes = Object.keys(layer.holes).filter(hid => {
            let hole = layer.holes[hid];
            let line = layer.lines[hole.line];
            return line.vertices.every(vid => layer.vertices[vid].areas.includes(areaId));
        }).map(hid => {
            let hole = layer.holes[hid];
            let line = layer.lines[hole.line];

            let indexOffset = area.vertices.indexOf(line.vertices[1]) - area.vertices.indexOf(line.vertices[0]);
            if (Math.abs(indexOffset) > 1) {
                indexOffset = -Math.sign(indexOffset);
            }

            let v0 = data.points[line.vertices[indexOffset === 1 ? 0 : 1]];
            let v1 = data.points[line.vertices[indexOffset === 1 ? 1 : 0]];

            let angle = Math.atan2(v1[1] - v0[1], v1[0] - v0[0]);

            v0 = data.points[line.vertices[0]];
            v1 = data.points[line.vertices[1]];
            let offset = hole.offset;
            let holeData = {
                id: hid,
                angle,
                scenes: hole.properties.connect.slice(),
                coord: [
                    v0[0] + (v1[0] - v0[0]) * offset,
                    v0[1] + (v1[1] - v0[1]) * offset
                ]
            };

            if (data.scene !== holeData.scenes[0]) {
                holeData.scenes.reverse();
            }

            return holeData;
        });

        return data;
    }

    /**
     * Get id mapping of "cuted" area.
     * @param layer
     * @param area
     * @returns {{vidMap, hidMap, lidMap}} - returns mapping of vertices, holes and lines in view: {oldId: newId}
     * @private
     */
    _getAreaMaps({layerFrom: layer, area}) {
        let verticesIdMap = {},
            linesIdMap = {},
            holesIdMap = {};

        //assigning maps with ids (map[oldId] => newId)
        area.vertices.forEach(vertexId => {
            let vertex = layer.vertices[vertexId];

            //if vertex is in common with another area then creating a new vertex id for copying
            if(vertex.areas.length === 1) {
                verticesIdMap[vertexId] = vertexId;
            }
            else {
                verticesIdMap[vertexId] = shortid.generate();
            }

            //if line is in common with another area(has two common vertices with another area) then creating a new line id for copying
            vertex.lines.forEach(lineId => {
                if(lineId in linesIdMap) return;
                let line = layer.lines[lineId];

                if(!line.vertices.every(vertexId => area.vertices.includes(vertexId))) return;
                if(line.vertices.every(vertexId => layer.vertices[vertexId].areas.length > 1)) {
                    linesIdMap[lineId] = shortid.generate();

                    //for each line in common with another area need to clone holes
                    line.holes.forEach(holeId => holesIdMap[holeId] = shortid.generate());
                }
                else {
                    linesIdMap[lineId] = lineId;
                    line.holes.forEach(holeId => holesIdMap[holeId] = holeId);
                }
            });
        });
        return {verticesIdMap, holesIdMap, linesIdMap};
    }

    /**
     * Get pair of point (floor and bottom) in spherical coordinates
     * @param {number} yaw
     * @param {number} pitch
     * @param {number} poseRoll
     * @param {number} posePitch
     * @returns {[*]} pair of top and bottom Euler coordinates
     */
    _getFloorpointPair(yaw, pitch, {poseRoll, posePitch}) {
        let bottom, top;
        let pitchRatio = this._getPitchRatio(this._layerId, this._areaId);

        // name roll _ pitch

        // poseRoll = 0.1;
        // poseRoll = -15 * Math.PI / 180;
        // poseRoll = -15 * Math.PI / 180;
        // posePitch *= 2;
        // posePitch = -10 * Math.PI / 180;

        // poseRoll += posePitch;
        // posePitch = poseRoll - posePitch;
        // poseRoll = poseRoll - posePitch;

        // posePitch *= -1;
        // poseRoll *= -1;


        yaw = FloorplanManager.normalizeYaw(yaw);

        if (pitch < 0) {
            let [x, y, z] = FloorplanManager.spher2Cart(yaw, pitch, poseRoll, posePitch);

            bottom = [yaw, pitch];
            top = FloorplanManager.cart2Spher(x,y,-z * pitchRatio, poseRoll, posePitch);

        } else {
            let [x, y, z] = FloorplanManager.spher2Cart(yaw, pitch, poseRoll, posePitch);

            top = [yaw, pitch];
            bottom = FloorplanManager.cart2Spher(x,y, -z / pitchRatio, poseRoll, posePitch);
        }

        return [bottom, top];
    }

    getPitchRatio() {
        return this._getPitchRatio();
    }
    /**
     * Get pitchRatio of the given area
     * @param {string} layerId
     * @param {string} areaId
     * @returns {number}
     * @private
     */
    _getPitchRatio(layerId = this._layerId, areaId = this._areaId) {
        let layer = this._floorplan.layers[layerId];
        let area = layer.areas[areaId];
        let {ceiling} = area.properties.scene;

        ceiling = Number(ceiling);
        const height_lens = FloorplanManager.getHeightLens(area);

        let pitchRatio = this._pitchRatio ? this._pitchRatio : (ceiling - height_lens) / (height_lens) ;

        return this._pitchRatio = pitchRatio < 0.1 ? 0.1 : pitchRatio;
    }
    /**
     * Set pitchRatio of the given area
     * @param {string} layerId
     * @param {string} areaId
     * @returns {number}
     * @private
     */

    setPitchRatio(pitchRatio) {
        this._pitchRatio = pitchRatio
    }

    /**
     * Get snap data, if it available
     * @param {number|NULL} vertexIndex
     * @param {number|NULL} snapVertexIndex
     * @returns {*[]} [angle]
     * @private
     */
    _getSnapData(vertexIndex = null, snapVertexIndex = null) {
        let vertex;
        let layer = this._floorplan.layers[this._layerId];
        let area = layer.areas[this._areaId];
        if (vertexIndex === null) {
            if (this._ghostCubeCoord && this._ghostCubeCoord.snapData) {
                return this._ghostCubeCoord.snapData;
            }
        } else {
            vertex = this._vertices[vertexIndex];
            if (vertex.snapData) {
                return vertex.snapData;
            }
        }
        let v1, v2, lastIndex;

        if (vertexIndex === null) {
            lastIndex = area.vertices.length;
        } else {
            lastIndex = vertexIndex;
        }
        if (snapVertexIndex) {
            v1 = this._vertices[snapVertexIndex];
            v2 = this._vertices[snapVertexIndex - 1];
        } else {
            let vertexIndexToSnap = lastIndex - 2;

            v1 = this._vertices[vertexIndexToSnap < 0 ? this._vertices.length + vertexIndexToSnap : vertexIndexToSnap];
            vertexIndexToSnap = lastIndex - 1;
            v2 = this._vertices[vertexIndexToSnap < 0 ? this._vertices.length + vertexIndexToSnap : vertexIndexToSnap];
        }

        // angle between line and OX axis on plane
        let angle = FloorplanManager.get2DLineAngle(v1.coord2d, v2.coord2d);

        return [angle, v2.coord2d.slice()];
    }

    /**
     *  "Cut" area from given layer and move to target layer (in case of changing floor of current area)
     * @param {string} areaId
     * @param {string} fromLayerId
     * @param {object} floor
     * @param {string} floor.id
     * @param {string} floor.label
     * @param {number} floor.altitude
     * @private
     */
    _moveArea(areaId, fromLayerId, floor) {
        let layerFrom = this._floorplan.layers[fromLayerId];
        let layerTo = this._floorplan.layers[floor.id];
        let layerPreviewFrom = this._floorplanPreview.layers[fromLayerId];
        let layerPreviewTo = this._floorplanPreview.layers[floor.id];

        let area = layerFrom.areas[areaId];
        if(!area) return ;
        let {verticesIdMap, linesIdMap, holesIdMap} = this._getAreaMaps({layerFrom, area});

        this._moveHoles({layerFrom, layerTo, holesIdMap, linesIdMap, layerPreviewFrom, layerPreviewTo});

        this._moveLines(layerFrom, layerTo, linesIdMap, holesIdMap, verticesIdMap, layerPreviewFrom, layerPreviewTo);

        this._moveVertices(layerFrom, layerTo, area, verticesIdMap, linesIdMap, layerPreviewFrom, layerPreviewTo);

        delete layerFrom.areas[areaId];
        layerTo.areas[areaId] = area;
    }

    /**
     * "Cut" holes from given layer and move to target layer (in case of changing floor of current area)
     * @param {object} layerFrom
     * @param {object}  layerTo
     * @param {object} hidMap
     * @param {object} lidMap
     * @private
     */
    _moveHoles({layerFrom, layerTo, holesIdMap: hidMap, linesIdMap: lidMap, layerPreviewFrom, layerPreviewTo}){
        for(let hid in hidMap) {
            let newHid = hidMap[hid];
            let hole = layerFrom.holes[hid];
            let holePreview = layerPreviewFrom.holes[hid];

            if(newHid !== hid) {
                hole = cloneDeep(hole);
                holePreview = cloneDeep(holePreview);
            }
            else if(layerTo !== layerFrom) {
                delete layerFrom.holes[hid];
                delete layerPreviewFrom.holes[hid];
            }
            hole.id = newHid;
            hole.line = lidMap[hole.line];
            layerTo.holes[newHid] = hole;

            if(holePreview && layerPreviewTo){
                holePreview.id = newHid;
                holePreview.line = lidMap[hole.line];
                layerPreviewTo.holes[newHid] = hole;
            }
        }
    }

    /**
     * "Cut" lines from given layer and move to target layer (in case of changing floor of current area)
     * @param {object} layerFrom
     * @param {object} layerTo
     * @param {object} lidMap
     * @param {object} hidMap
     * @param {object} vidMap
     * @private
     */
    _moveLines(layerFrom, layerTo, lidMap, hidMap, vidMap, layerPreviewFrom, layerPreviewTo) {
        for(let lid in lidMap) {
            let newLid = lidMap[lid];
            let line = layerFrom.lines[lid];
            if(newLid !== lid) {
                line = cloneDeep(line);
            }
            else if (layerTo !== layerFrom) {
                delete layerFrom.lines[lid];
            }
            line.id = newLid;
            line.holes = line.holes.map(hid => hidMap[hid]);
            line.vertices = line.vertices.map(vid => vidMap[vid]);
            layerTo.lines[newLid] = line;
        }
    }

    /**
     * "Cut" vertices of given layer and move it to target layer (in case of changing floor of current area)
     * @param layerFrom
     * @param layerTo
     * @param area
     * @param vidMap
     * @param lidMap
     * @private
     */
    _moveVertices(layerFrom, layerTo, area, vidMap, lidMap, layerPreviewFrom, layerPreviewTo) {
        for(let vid in vidMap) {
            let newVid = vidMap[vid];
            let vertex = layerFrom.vertices[vid];
            if(newVid !== vid) {
                vertex.areas.splice(vertex.areas.indexOf(area.id), 1);
                vertex = cloneDeep(vertex);
                vertex.areas = [area.id];
            }
            else if(layerFrom !== layerTo){
                delete layerFrom.vertices[vid];
            }
            vertex.id = newVid;
            vertex.lines = vertex.lines.map(lid => lidMap[lid]).filter(Boolean);
            layerTo.vertices[newVid] = vertex;
        }
        area.vertices = area.vertices.map(vid => vidMap[vid]);
    }

    /**
     * Init floorplan object from config
     * @param {object} config
     * @param {boolean} fromEditor
     * @param {Object[]} scenes
     * @param {string} scenes[].id
     * @param {string} scenes[].name
     */
    _prepareFloorplan(floorplan, {scenes, init}, title) {
        let floorplanPreview, changePreview;

        if (!floorplan) {
            floorplan = {
                unit: 'mm',
                width: 0,
                height: 0,
                customUpdate: false,
                layers: {},
            };

            // floorplanPreview = {};
        }
        else {
            floorplanPreview = floorplan.preview;
            if (floorplanPreview.selectedLayer !== floorplan.selectedLayer)
                floorplanPreview.selectedLayer = floorplan.selectedLayer;

            delete floorplan.preview;
        }

        let layer;
        if (!Object.keys(floorplan.layers).length) {
            layer = this._createLayer(title || 'Floor Name');
            floorplan.layers[layer.id] = layer;
        }
        else {
            for (let l in floorplan.layers) {
                layer = floorplan.layers[l];
                if (layer.name === 'Floor Name') break;
            }
        }

        if (!init) ({changePreview, floorplan} = this._checkTourScenesChanges(floorplan, scenes, layer));
        if (!floorplanPreview) floorplanPreview = floorplan;

        return {floorplan, floorplanPreview, changePreview };
    }

    removeArea(areaID) {
        let layers = Object.values(this._floorplan.layers);
        let layerID = null;
        layers.forEach((layer) => {
            if(Object.keys(layer.areas).includes(areaID)) layerID = layer.id;
        })

        if (layerID) {
            let area = this._floorplan.layers[layerID].areas[areaID];
            this._removeAreaContent(layerID, areaID);
            delete this._floorplan.layers[layerID].areas[areaID];
            this._removeHoleFromLayer(this._floorplan.layers[layerID], area.properties.scene.id);
        }
    }

    _checkTourScenesChanges(floorplan, scenes, activeLayer) {
        let changePreview = false;
        let areas = [];

        for (let k in floorplan.layers) {
            let l = floorplan.layers[k];
            areas.push(...Object.keys(l.areas).map(areaId => l.areas[areaId]));
        }
        // if(fromEditor) {
        //     //reverse vertices direction (clockwise yaw)
        //     areas.forEach(area => area.vertices.reverse());
        // }
        scenes.forEach(scene => {
            let area = areas.find(area => scene.id === area.properties.scene.id);
            if (!area) {
                area = this._createArea({
                    sceneID: scene.id,
                    sceneName: scene.name,
                    hotSpots: scene.hotSpots,
                    panorama: scene.panorama
                });

                activeLayer.areas[area.id] = area;
            } else {
                area.properties.scene.panorama = scene.panorama;
                area.properties.scene.name = scene.name;
            }

            area.checked = true;
        });

        areas.forEach(area => {
            if (!area.checked) {
                // remove area
                let layerID;
                for (let id in floorplan.layers) {
                    if (floorplan.layers.hasOwnProperty(id)) {
                        let res = Object.keys(floorplan.layers[id].areas).find(k => {
                            return floorplan.layers[id].areas[k].properties.scene.id === area.properties.scene.id
                        });

                        if (res) {
                            layerID = id;
                            break;
                        }
                    }
                }

                if (layerID) {
                    this._removeAreaContent(layerID, area.id, floorplan);
                    delete floorplan.layers[layerID].areas[area.id];
                    this._removeHoleFromLayer(floorplan.layers[layerID], area.properties.scene.id);
                }

                changePreview = true;
            }

            delete area.checked;
        });

        return {changePreview, floorplan}
    }

    /**
     * Clean up holes
     * @private
     */
    _removeHoleFromLayer(layer, hotspotId) {
        let holes = Object.values(layer.holes);
        let deletedHoles = holes.filter((hole) => hole.properties.connect.indexOf(hotspotId) !== -1);
        deletedHoles.forEach((hole) => {
            delete layer.holes[hole.id];
            layer.lines[hole.line].holes = layer.lines[hole.line].holes.filter((id) => id !== hole.id)
        })
    }

    /**
     * Clean up target area
     * @param layerId
     * @param areaId
     * @param floorplan
     * @private
     */
    _removeAreaContent(layerId = this._layerId, areaId = this._areaId, floorplan = null) {
        floorplan = floorplan ? floorplan : this._floorplan;
        let layer = floorplan.layers[layerId];
        let area = layer.areas[areaId];
        area.vertices.forEach(vid => {
            layer.vertices[vid].lines.forEach(lid => {
                if (layer.lines[lid]) {
                    layer.lines[lid].holes.forEach(hid => {
                        delete layer.holes[hid];
                    });
                    delete layer.lines[lid];
                }
            });
            delete layer.vertices[vid];
        });
        area.vertices = [];
    }

    /**
     * Clean up holes of current area
     * @private
     */
    _removeHoles() {
        let layer = this._floorplan.layers[this._layerId];
        let area = layer.areas[this._areaId];
        let vids = area.vertices.slice();
        vids.push(vids[0]);
        vids.reduce((v1Id, v2Id) => {
            let v1 = layer.vertices[v1Id];
            let v2 = layer.vertices[v2Id];
            let line = layer.lines[v1.lines.find(lid => v2.lines.includes(lid))];
            line.holes.forEach(hid => delete layer.holes[hid]);
            line.holes = [];

            return v2Id;
        });
    }

    /**
     * Update  all scene props from input config
     * @param {object} props
     * @public
     */
    setAllScenesProps(props) {
        let opts = Object.keys(props);

        for (let l in this._floorplan.layers) {
            let layer = this._floorplan.layers[l];
            for (let a in layer.areas) {
                let area = layer.areas[a];
                opts.forEach(opt => {
                    area.properties.scene[opt] = props[opt];
                })
            }
        }
        this._updateFloorplan();
    }

    /**
     * Update scene props from input config
     * @param {object} props
     * @private
     */
    _setCurrentSceneProps(props, update= false) {
        let layer = this._floorplan.layers[this._layerId];
        let area = layer.areas[this._areaId];
        let opts = Object.keys(props);
        opts.forEach(opt => {
            switch (opt) {
                case 'floors': {
                    if (props[opt].id !== this._floorplanPreview.selectedLayer) {
                        this._floorplanPreview.selectedLayer = props[opt].id;
                    }

                    break;
                }
                case 'ceiling': {
                    area.vertices.forEach(vid => {
                        let v = layer.vertices[vid];
                        v.lines.forEach(lid => {
                            layer.lines[lid].properties.height.length = props[opt];
                        });
                    });

                    this._wallHeight = props[opt];
                }
                default: {
                    area.properties.scene[opt] = props[opt];
                }
            }
        });

        this._pitchRatio = this._getPitchRatio(this._layerId, this._areaId);
        this._recalculateVerticesPairs(props.isTopPoint);
        this._updateFloorplan(update);
    }

    _getVerticesFromArea(layerId = this._layerId, areaId = this._areaId, ids) {
        let layer = this._floorplan.layers[layerId];
        let area = layer.areas[areaId];
        let verticesCoord = this._getAreaLocalCoords(layer, areaId, true);
        let vertices = [];
        this._pitchRatio = this._getPitchRatio(this._layerId, this._areaId);
        for (let i = 0 ; i < ids.length; i++) {
            let vid = ids[i];
            // let coordsEqui = FloorplanManager.point2DToSpher(...verticesCoord[vid], area.properties.scene);
            let coordsEqui = FloorplanManager.point2DToSphere(...verticesCoord[vid], area.properties.scene);
            let pair = this._getFloorpointPair(...coordsEqui, area.properties.scene);
            vertices.push({
                coord2d: verticesCoord[vid],
                floorSpher: pair[0],
                ceilingSpher: pair[1],
                floorCube: CubemapUtil.getBoxCoords(...pair[0]),
                ceilingCube: CubemapUtil.getBoxCoords(...pair[1])
            });
        }

        return vertices;
    }

    _setHoles(layerId = this._layerId,
              areaId = this._areaId,
              hotspots = Object.values(this._hotspots),
              vertices = this._vertices.slice(),
              ids,
              checked = true) {
        let layer = this._floorplan.layers[layerId];
        let area = layer.areas[areaId];
        ids = ids ? ids: area.vertices.slice();
        ids.push(ids[0]);
        let PI2 = Math.PI * 2;

        ids.reduce((v1Id, v2Id, i) => {
            let v1 = vertices[area.vertices.indexOf(v1Id)],
                v2 = vertices[area.vertices.indexOf(v2Id)],
                yaw1 = v1.floorSpher[0],
                yaw2 = v2.floorSpher[0],
                pitch1 = v1.floorSpher[1],
                pitch2 = v2.floorSpher[1];

            hotspots = hotspots.filter(hs => {
                let y = FloorplanManager.normalizeYaw(hs.equiCoord[0]);

                let y1 = yaw1;
                let y2 = yaw2;
                let offset = FloorplanManager.getHotspotOffset(y, y1, y2);

                if (offset >= 0 && offset <= 1) {
                    let hotspot = {yaw: hs.equiCoord[0], pitch: hs.equiCoord[1]};
                    let ratio = FloorplanManager.findHoleOffset(yaw1, pitch1, yaw2, pitch2, hotspot.yaw);
                    v1 = layer.vertices[v1Id];
                    v2 = layer.vertices[v2Id];
                    let lineId = v1.lines.find(lid => v2.lines.includes(lid));
                    let line = layer.lines[lineId];
                    let hole = this._createHole({
                        lineId,
                        link: hs.link,
                        offset: v1Id === line.vertices[0] ? ratio : 1 - ratio,
                        hotSpotId: hs.id
                    }, areaId, checked);
                    line.holes.push(hole.id);
                    return false;
                }
                return true;
            });
            return v2Id;
        });
    }

    /**
     * Calculate [yaw, pitch] point, which is snapped according to FloorplanManager.snapAngles data
     * @param {number} yaw
     * @param {number} pitch
     * @param {array} snapData - [0] - angel between line of previous two points and OX
     *                           [1] - 2D coordinates of last point
     * @returns {number[]}
     * @private
     */
    _snap(yaw, pitch, snapData) {
        let sceneProps = this._floorplan.layers[this._layerId].areas[this._areaId].properties.scene;
        let {poseRoll, posePitch} = sceneProps;
        let coord2d = FloorplanManager.sphereTo2dCorrected(yaw, pitch, {poseRoll, posePitch});
        let angle = FloorplanManager.get2DLineAngle(snapData[1], coord2d);
        let snapAngleDelta = FloorplanManager.normalizeYaw(Math.PI - (angle - snapData[0]));
        let angleIsNegative = snapAngleDelta < 0;
        if(angleIsNegative) {
            snapAngleDelta = -snapAngleDelta;
        }
        for(let i = 0 ; i < FloorplanManager.snapAngles.length; i++) {
            let snapAngleData = FloorplanManager.snapAngles[i];
            if(snapAngleDelta > snapAngleData[1] && snapAngleDelta <= snapAngleData[2]) {
                snapAngleDelta = snapAngleData[0];
                break;
            }
        }
        if(angleIsNegative) {
            snapAngleDelta = -snapAngleDelta;
        }
        let a = Math.PI + snapData[0] - snapAngleDelta;
        let k = Math.tan(a);
        let b = snapData[1][1] - k * snapData[1][0];
        let kPerpendicular = -1 / k;
        let bPerpendicular = coord2d[1] - kPerpendicular * coord2d[0];
        let x = (b - bPerpendicular) / (kPerpendicular - k);
        let y = kPerpendicular * x + bPerpendicular;

        return FloorplanManager.point2DToSphere(x, y, {poseRoll, posePitch});
    }

    /**
     * Adding snapData for vertex based on current vertex index, and index of point to snap
     * @param {number|NULL} index
     * @param {number|NULL} snapVertexIndex
     * @private
     */
    _snapVertex(index = null, snapVertexIndex = null) {
        if(this._vertices.length < 2) return;
        let vertex = this._vertices[index];
        vertex.snapData = this._getSnapData(this._vertices.length === 2 ? null : index, snapVertexIndex);
        let newCoords = this._snap(vertex.floorSpher[0], vertex.floorSpher[1], vertex.snapData);
        this.updateVertexView(
            newCoords,
            index,
            false,
            true,
            true
        );

    }

    /**
     * Change point order in line based on _checkSequence rule
     * @param {string} lineId
     * @param {string} layerId
     * @private
     */
    _updateLineVerticesSequence(lineId, layerId = this._layerId) {
        let layer = this._floorplan.layers[layerId];
        let line = layer.lines[lineId];
        let v1, v2;

        v1 = layer.vertices[line.vertices[0]];
        v2 = layer.vertices[line.vertices[1]];
        v1 = [v1.x, v1.y];
        v2 = [v2.x, v2.y];

        if (!this._checkSequence(v1, v2)) {
            line.vertices.reverse();
            line.holes.forEach(hid => {
                let hole = layer.holes[hid];
                hole.offset = 1 - hole.offset;
            });
        }
    }

    /**
     * Update current area view from floorplan object
     * @param {string} layerId
     * @private
     */

    _updateFloorplan(updatePosition = true) {
        if(this._floorplanPreview.customUpdate) return this._updateFloorplanForCustomUpdate(updatePosition);

        const layerId = this._layerId;
        const layer = this._floorplan.layers[layerId];
        this._updateFloorplanData(layer);

        this._floorplan.selectedLayer = layerId;
        FloorplanManager.normalizeData(this._floorplan);
    }

    _updateFloorplans() {
        Object.values(this._floorplan.layers)
            .forEach((layer) => {
                this._updateFloorplanData(layer);
            })

        this._floorplan.selectedLayer = this._layerId;
        FloorplanManager.normalizeData(this._floorplan);
    }

    _updateFloorplanData(layer) {
        layer.angle = 0;
        let data = {};
        for (let a in layer.areas) {
            if (layer.areas.hasOwnProperty(a)) {
                data[a] = this._getAreaLocalData(layer, a);
            }
        }

        for (let a in layer.areas) {
            if (layer.areas.hasOwnProperty(a) && layer.areas[a].vertices.length > 1) {
                this._updateAreaPointsRecursively(data, a, [0, 0], 0, null, layer);
            }
        }

        for (let aid in data) {
            let area = layer.areas[aid];
            let areaData = data[aid];
            area.properties.scene.rotateDirection = areaData.direction;
            area.properties.scene.angleForRotateCustomUpdate = 0;
            area.properties.scene.camera = {x: areaData.camera[0], y: areaData.camera[1]};
            for (let vid in areaData.points) {
                if (areaData.points.hasOwnProperty(vid)) {
                    layer.vertices[vid].x = areaData.points[vid][0];
                    layer.vertices[vid].y = areaData.points[vid][1];
                    if(areaData.cartCoords) layer.vertices[vid]['2d'] = areaData.cartCoords[vid]['2d'];
                }
            }
        }

        Object.keys(layer.lines).forEach(lid => this._updateLineVerticesSequence(lid, layer.id));
    }

    _updateFloorplanForCustomUpdate(updatePosition) {
        const layerId = this._layerId;
        const areaId = this._areaId;
        const areasKeys = [areaId];
        const layer = this._floorplan.layers[layerId];
        const angle = updatePosition ? clone(layer.areas[areasKeys[0]].properties.scene.angle) : 0;
        let camera = 0;
        let rotateDirection = 0;
        let ceiling = 0;
        let width = {};
        let length = {};
        let area = {};
        let fontSize = FONT_SIZE;

        layer.angle = 0;
        let data = {};
        for (let a in layer.areas) {
            if (areasKeys.includes(a) && layer.areas.hasOwnProperty(a)) {
                data[a] = this._getAreaLocalData(layer, a);
            }
        }

        for (let a in layer.areas) {
            if (areasKeys.includes(a) && layer.areas.hasOwnProperty(a) && layer.areas[a].vertices.length > 1) {
                this._updateAreaPointsRecursively(data, a, [0, 0], 0, null, layer);
            }
        }

        for (let aid in data) {
            if(areasKeys.includes(aid)){
                let area = layer.areas[aid];
                let areaData = data[aid];
                area.properties.scene.rotateDirection = areaData.direction;
                area.properties.scene.camera = {x: areaData.camera[0], y: areaData.camera[1]};

                ceiling = area.properties.scene.ceiling;
                camera = area.properties.scene.camera;
                rotateDirection = area.properties.scene.rotateDirection;

                for (let vid in areaData.points) {
                    if (areaData.points.hasOwnProperty(vid)) {
                        layer.vertices[vid].x = areaData.points[vid][0];
                        layer.vertices[vid].y = areaData.points[vid][1];
                        if(areaData.cartCoords) layer.vertices[vid]['2d'] = areaData.cartCoords[vid]['2d'];
                    }
                }
            }
        }

        Object.keys(layer.lines).forEach(lid => this._updateLineVerticesSequence(lid, layer.id));

        this._floorplan.selectedLayer = layerId;
        FloorplanManager.normalizeData(this._floorplan);
        const sceneProperties = {camera, angle, rotateDirection, ceiling};
        const properties = {fontSize, length, area, width};

        if(updatePosition) this.updateScenePosPreview(areasKeys[0], sceneProperties);
        if(updatePosition) this.updatePosPreview(areasKeys[0], properties);
    }

    setAngleForRotateCustomUpdate(layerId){
        const layer = this._floorplan.layers[layerId];
        const area = layer.areas[this._areaId];
        area.properties.scene.angleForRotateCustomUpdate = 0;

        const preview = this._floorplanPreview.layers[layerId];
        if(preview){
            const { angle } = preview;

            if(angle) {
                area.properties.scene.angleForRotateCustomUpdate = -1 * angle / 180 * Math.PI;
            }
        }
    }

    updateScenePosPreview(id, properties){
        let previewLayer = this._floorplanPreview.layers[this._layerId];
        if(!previewLayer || !previewLayer.areas[id]) return ;

        for(let obj in properties){
            previewLayer.areas[id].properties.scene[obj] = properties[obj];
        }
    }

    updatePosPreview(id, properties){
        let previewLayer = this._floorplanPreview.layers[this._layerId];
        if(!previewLayer || !previewLayer.areas[id]) return ;

        for(let obj in properties){
            previewLayer.areas[id].properties[obj] = properties[obj];
        }
    }

    /**
     * Update 2D room view
     * @param {object} areasData
     * @param {string} areaId
     * @param {number[]} translation
     * @param {number} angle
     * @param {string} areaIdFrom - id of area, that was updated before this area
     * @private
     */
    _updateAreaPoints(areasData, areaId, translation, angle, areaIdFrom, layer) {
        let areaData = areasData[areaId];
        // let {height_lens, tripodHeight, cameraHeight} = areaData;
        let {height_lens} = areaData;
        let hole;
        // let area = layer.areas[areaId];
        if (areaIdFrom !== null) {
            let {scene: sceneIdFrom} = areasData[areaIdFrom];
            hole = areaData.holes.find(hole => {
                return hole.scenes[1] === sceneIdFrom;
            });
            if (hole) {
                angle -= hole.angle;
            }
        }
        else {
            angle = areaData.holes.length ? -areaData.holes[0].angle : 0;
        }

        areaData.cartCoords = this._getCartCoords(layer, areaId, areaData.points);

        FloorplanManager.rotate2DArea(areaData, angle);

        areaData.direction = FloorplanManager.normalizeYaw(angle);
        let translationClone = _.cloneDeep(translation);
        // translate clone data for check direction between 2 rooms
        if (hole) {
            // translationClone[0] -= hole.coord[0] * (areaData.tripodHeight + cameraHeight);
            // translationClone[1] -= hole.coord[1] * (areaData.tripodHeight + cameraHeight);
            translationClone[0] -= hole.coord[0] * (height_lens);
            translationClone[1] -= hole.coord[1] * (height_lens);
        }
        let cloneAreaData = _.cloneDeep(areaData);

        FloorplanManager.scale2DArea(cloneAreaData, height_lens);
        FloorplanManager.translate2DArea(cloneAreaData, translationClone);


        if (hole) {
            let holeCurrent = layer.holes[hole.id];
            let line = layer.lines[holeCurrent.line];

            let v1 = cloneAreaData.points[line.vertices[0]];
            let v2 = cloneAreaData.points[line.vertices[1]];
            let x1 = Math.ceil(v1[0]);
            let y1 = Math.ceil(v1[1]);
            let x2 = Math.ceil(v2[0]);
            let y2 = Math.ceil(v2[1]);
            let holewidthX = x1 == x2 || Math.abs(Math.abs(x1) - Math.abs(x2))<30  ? 1 : 0;
            let holewidthY = y1 == y2 || Math.abs(Math.abs(y1) - Math.abs(y2))<30  ? 1 : 0;

            let areaDataFrom = areasData[areaIdFrom];
            let d1 = (cloneAreaData.camera[0]-areaDataFrom.camera[0]) > 0 ? -1 * FloorplanManager.thickness : FloorplanManager.thickness;
            let d2 = (cloneAreaData.camera[1]-areaDataFrom.camera[1]) > 0 ? -1 * FloorplanManager.thickness : FloorplanManager.thickness;
            translation[0] -= holewidthX ? d1 : 0;
            translation[1] -= holewidthY ? d2 : 0;
            // translation[0] -= d1;
            // translation[1] -= d2;
            translation[0] -= hole.coord[0] * (height_lens);
            translation[1] -= hole.coord[1] * (height_lens);
        }

        FloorplanManager.scale2DArea(areaData, height_lens);
        FloorplanManager.translate2DArea(areaData, translation);

    }

    /**
     * Recursion, which runs through the areas by theirs holes and
     * rotate (<angle> value), scale (<tripodHeight> value) and translate (<translation> value) them.
     * @param {object} areasData
     * @param {string} areaId
     * @param {number[]} translation
     * @param {number} angle
     * @param {string} areaIdFrom - need to keep track of already updated areas
     * @private
     */
    _updateAreaPointsRecursively(areasData, areaId, translation, angle, areaIdFrom, layer) {
        let areaData = areasData[areaId];
        if (areaData.generated) return;

        this._updateAreaPoints(areasData, areaId, translation, angle, areaIdFrom, layer);
        areaData.generated = true;

        areaData.holes.forEach(hole => {
            let sceneIdTo = hole.scenes[1];
            if (sceneIdTo === '0') return;
            let areaIdTo;
            for (let a in areasData) {
                if (areasData[a].scene === sceneIdTo) {
                    areaIdTo = a;
                    break;
                }
            }

            if (typeof areaIdTo === 'undefined') return;

            let rotateAngle = FloorplanManager.normalizeYaw(hole.angle + Math.PI);

            this._updateAreaPointsRecursively(areasData, areaIdTo, hole.coord, rotateAngle, areaId, layer);
        });

    }

    /**
     * checkSnapVertex
     * @param vertexIndex, snapVertexIndex=1
     */
    checkSnapVertex(index, snapVertexIndex=1) {
        if(this._vertices.length < 2) return;
        let prev = this._vertices[index];
        let current = this._vertices[this._vertices.length-1 < snapVertexIndex+1 ? 0 : snapVertexIndex+1];
        let mid = this._vertices[snapVertexIndex];

        let angle = FloorplanManager.angleBetween2DLines({x:prev.coord2d[0], y:prev.coord2d[1]}, {x:current.coord2d[0],y: current.coord2d[1]}, {x:mid.coord2d[0], y: mid.coord2d[1]}) / Pannellum.RADIAN;

        return Math.round(angle);
    }

    removeVertice(ind){
        this._vertices.splice(ind, 1);
    }

    /**
     * Correct last drown point position related to 1st point of sequence using snap angles
     * @param vertexIndex
     */
    lastDrownPointCorrection(vertexIndex) {
        this._snapVertex(vertexIndex, 1);
    }

    toggleSnapMode(mode){
        this._snapMode = mode || !this._snapMode;
    }

    /**
     * Add layout json to current area
     * @param {number[]} spherCoord - pair of yaw and pitch
     */
    addLayoutJson(layoutJson, sceneID) {
        let layer = this._floorplan.layers[this._layerId];
        let areas = Object.values(layer.areas);

        areas.forEach((area, ind) => {
            if(sceneID == area.properties.scene.id){
                area.layoutJson = layoutJson;
            }
        })
    }

    /**
     * Add point to current area
     * @param {number[]} spherCoord - pair of yaw and pitch
     */
    addPoint(spherCoord) {
        let layer = this._floorplan.layers[this._layerId];
        let area = layer.areas[this._areaId];
        let pair;

        if (this._ghostCubeCoord) {
            pair = this._ghostCubeCoord.spherCoords;
        }
        else {
            if (this._snapMode) {
                pair = this.setGhostPoint(spherCoord);
                this.removeGhostPoint();
            }
            else {
                pair = this._getFloorpointPair(...spherCoord.map(v => v * Pannellum.RADIAN), area.properties.scene);
            }
        }

        this._vertices.push({
            floorSpher: pair[0],
            ceilingSpher: pair[1],
            floorCube: CubemapUtil.getBoxCoords(...pair[0]),
            ceilingCube: CubemapUtil.getBoxCoords(...pair[1]),
            // coord2d: FloorplanManager.sphereTo2dNormalized(...pair[0], area.properties.scene),
            coord2d: FloorplanManager.sphereTo2dCorrected(...pair[0], area.properties.scene),
        });
    }

    /**
     * Check if able to add new point to current area
     * @returns {boolean}
     */
    canAddPoint() {
        return this._layerId && this._areaId;
    }

    /**
     * Clean up area from vertices, holes, lines
     */
    clearArea() {
        for (let l in this._floorplan.layers) {
            let layer = this._floorplan.layers[l];
            for (let a in layer.areas) {
                if(this._areaId === a){
                    let area = layer.areas[a];
                    let linesId = [];
                    let holesId = [];
                    let verticesId = [];
                    area.vertices.map((val) => {
                        layer.vertices[val].lines.map((line) => {
                            layer.lines[line].holes.map((hole) => holesId.push(hole));
                            if(linesId.indexOf(line) === -1) linesId.push(line);
                        });
                        verticesId.push(val);
                    });
                    holesId.map((val) =>  delete layer.holes[val]);
                    linesId.map((val) =>  delete layer.lines[val]);
                    verticesId.map((val) =>  delete layer.vertices[val]);

                    this._vertices = [];
                    delete layer.areas[a];

                    layer.areas[a] = this._createArea({
                        sceneID: area.properties.scene.id,
                        sceneName: area.properties.scene.name,
                        areaID: a,
                        height_lens: this.getObjHeightLens(area),
                        angleForRotateCustomUpdate: area.properties.scene.angleForRotateCustomUpdate,
                        ceiling: area.properties.scene.ceiling,
                        addToLayer: false,
                        panorama: area.properties.scene.panorama,
                        posePitch: area.properties.scene.posePitch,
                        poseRoll: area.properties.scene.poseRoll,
                    });
                    break;
                }
            }
        }

    }

    /**
     * Close path of drawing area
     */
    closePath(update=true) {
        this._removeAreaContent();
        this._createAreaBasedOnVertices();
        this._updateFloorplan(update);
    }

    /**
     * Get cubeBox coordinates for each vertex in _vertices array.
     * Needed for drawing points by webGL on cube faces
     * @returns {{points: Array, hasGhostPoint: boolean, finished: *}}
     */
    getCubeVertices() {
        let retObj = {points: [], hasGhostPoint: !!this._ghostCubeCoord, finished: this.isDrawingFinished()};
        if (!this._layerId || !this._areaId) return retObj;

        let vertices = this._vertices.map(data => [data.floorCube, data.ceilingCube]);
        if (this._ghostCubeCoord) {
            vertices.push(this._ghostCubeCoord.coords);
        }

        retObj.border = this._ghostCubeCoordPerpendicularLine && this._ghostCubeCoordPerpendicularLine.map((val) => val.coords);
        retObj.points = vertices;
        return retObj;
    }

    /**
     * Return copy of whole floorplan object
     * @returns {object}
     */
    getFloorplan() {
        return cloneDeep(this._floorplan);
    }

    /**
     * Returns copy of floorplan preview
     * (needs for preview purposes of react-planner)
     * @returns {*}
     */
    getFloorplanPreview() {
        if (this._floorplanPreview.selectedLayer !== this._floorplan.selectedLayer) {
            this._floorplanPreview.selectedLayer = this._floorplan.selectedLayer;
        }
        return cloneDeep(this._floorplanPreview);
    }

    setFloorplanPreview(plan) {
        this._floorplanPreview = cloneDeep(plan);
    }

    /**
     * Return mapping of available layers
     * @returns {{id: string, name: *}[]}
     */
    getFloors() {
        return Object.keys(this._floorplan.layers).map(id => {
            return {id, name: this._floorplan.layers[id].name}
        });
    }

    /**
     * Return properties of current area
     * @returns {*}
     */
    getSceneProps() {
        if (!this._layerId || !this._areaId) return {};
        let layer = this._floorplan.layers[this._layerId];
        let area = layer.areas[this._areaId];
        let {ceiling} = pick(area.properties.scene, ["ceiling"]);

        const height_lens = this.getObjHeightLens(area);

        return Object.assign({ceiling, height_lens}, {floors: {id: layer.id, label: layer.name}});
    }

    /**
     * Return current floor
     * @returns {*}
     */
    getCurrentFloor() {
        if (!this._layerId || !this._areaId) return {};
        let layer = this._floorplan.layers[this._layerId];

        return Object.assign({},{id: layer.id, name: layer.name});
    }

    /**
     * Return quantity of vertices in current area
     * @returns {number}
     */
    getVertexCount() {
        return this._vertices.length;
    }

    /**
     * Return spherical coordinates of current vertices
     * @returns {Array}
     */
    getVerticesSpherCoords() {
        if (!this._layerId || !this._areaId) return [];
        return this._vertices.map(data => [data.floorSpher, data.ceilingSpher]);
    }

    /**
     * Does process of drawing floorplan is finished
     * @returns {boolean}
     */
    isDrawingFinished() {
        return this._layerId && this._floorplan.layers[this._layerId].areas[this._areaId] && !!this._floorplan.layers[this._layerId].areas[this._areaId].vertices.length;
    }

    /**
     * Remove ghost (not drown, cursor position) point
     */
    removeGhostPoint() {
        this._ghostCubeCoord = null;
    }

    /**
     * Set value of current area ceiling height
     * @param {number} pitchRatio
     * @param {boolean} isTopPoint
     */
    setCeilling(ceiling) {
        let layer = this._floorplan.layers[this._layerId];
        let area = layer.areas[this._areaId];

        area.properties.scene = Object.assign(area.properties.scene, {ceiling});
    }

    isPreviewCustomUpdate(){
        return this._floorplanPreview.customUpdate;
    }

    /**
     * Set value of current area ceiling height
     * @param {number} pitchRatio
     * @param {boolean} isTopPoint
     */
    setCeilingHeight(pitchRatio, isTopPoint, layerId=this._layerId, areaId=this._areaId ) {
        this._pitchRatio = pitchRatio;
        let layer = this._floorplan.layers[layerId];
        let area = layer.areas[areaId];
        let height_lens = FloorplanManager.getHeightLens(area);
        let ceiling = (1 + pitchRatio) * (height_lens);

        area.properties.scene = Object.assign(area.properties.scene, {ceiling});

        if(this._floorplanPreview.customUpdate) this.closePath();

        area.vertices.forEach(vid => {
            layer.vertices[vid].lines.forEach(lid => {
                layer.lines[lid].properties.height.length = ceiling;
            });
        });

        this._recalculateVerticesPairs(isTopPoint);

        this._updateFloorplan();
    }

    // todo: deep review is needed
    /**
     * Calculate vertices pair for ghost (cursor pointer)
     * @param {Array<number>} coord
     * @param {number} coord[0] - yaw
     * @param {number} coord[1] - pitch
     */
    setGhostPoint(coord) {
        let layer = this._floorplan.layers[this._layerId];
        let area = layer.areas[this._areaId];
        let props = area.properties.scene;
        let pair = this._getFloorpointPair(...coord.map(c => c * Pannellum.RADIAN), props);
        let perpendicularRight = pair;
        let perpendicularLeft = pair;
        this._ghostCubeCoord = {};
        this._ghostCubeCoordPerpendicularLine = [];
        if (this._snapMode && this._vertices.length > 1) {
            this._ghostCubeCoord.snapData = this._getSnapData(null);
            pair = this._getFloorpointPair(...this._snap(...pair[0], this._ghostCubeCoord.snapData), props);

            let coord2dGhost = FloorplanManager.sphereTo2dCorrected(...pair[0], props);
            let coord2dEnd = this._vertices[this._vertices.length-1].coord2d.slice();
            let dist = 5 + 10 * Math.sqrt((coord2dEnd[0] - coord2dGhost[0])*(coord2dEnd[0] - coord2dGhost[0]) + (coord2dEnd[1] - coord2dGhost[1])*(coord2dEnd[1] - coord2dGhost[1]));

            let borderRight = FloorplanManager.perpendicular2DLine({x: coord2dGhost[0], y: coord2dGhost[1]}, {x: coord2dEnd[0], y: coord2dEnd[1]}, dist);
            perpendicularRight = this._getFloorpointPair(...FloorplanManager.point2DToSphere(borderRight[0], borderRight[1], props), props);

            let borderLeft = FloorplanManager.perpendicular2DLine({x: coord2dGhost[0], y: coord2dGhost[1]}, {x: coord2dEnd[0], y: coord2dEnd[1]}, -dist);
            perpendicularLeft = this._getFloorpointPair(...FloorplanManager.point2DToSphere(borderLeft[0], borderLeft[1], props), props);
        }
        this._ghostCubeCoord.spherCoords = pair;
        this._ghostCubeCoord.coords = [CubemapUtil.getBoxCoords(...pair[0]), CubemapUtil.getBoxCoords(...pair[1])];

        this._ghostCubeCoordPerpendicularLine.push({
            coords: [CubemapUtil.getBoxCoords(...perpendicularRight[0]), CubemapUtil.getBoxCoords(...perpendicularRight[1])],
        });

        this._ghostCubeCoordPerpendicularLine.push({
            coords: [CubemapUtil.getBoxCoords(...perpendicularLeft[0]), CubemapUtil.getBoxCoords(...perpendicularLeft[1])],
        });

        return pair;
    }

    getSceneId() {
        return this._sceneId;
    }

    getPoseData(){
        return this._pose;
    }

    /**
     * Set current area vertices, lines and holes, calls areaLocalData.
     * It's needed for floorplan drawing of current area
     * @param {string} sceneId
     * @param {string} sceneName
     * @param {object[]} hotspots
     * @param {number} poseRoll
     * @param {number} posePitch
     */
    setRoom(sceneId, sceneName, hotspots, poseRoll = 0, posePitch = 0) {
        this._sceneId = sceneId;
        this._sceneName = sceneName;
        this._hotspots = {};
        this._pose = {poseRoll, posePitch};
        // equiCoord comes from Pannellum, need Pannellum refactoring
        hotspots.forEach(hs => {
            hs.equiCoord = hs.equiCoord.map(v => v * Pannellum.RADIAN);
            this._hotspots[hs.id] = hs;
        });
        this._layerId = null;
        this._areaId = null;
        this._vertices = [];
        for (let l in this._floorplan.layers) {
            let layer = this._floorplan.layers[l];
            for (let a in layer.areas) {
                let area = layer.areas[a];
                if (area.properties.scene.id == sceneId) {
                    area.properties.scene.poseRoll = poseRoll;
                    area.properties.scene.posePitch = posePitch;
                    this._areaId = area.id;
                    this._layerId = layer.id;
                    this._moveArea(this._areaId, this._layerId, {id: this._layerId});
                    let vertices = this._getAreaLocalCoords(layer, this._areaId, true);
                    this._pitchRatio = this._getPitchRatio(this._layerId, this._areaId);
                    for (let i = 0 ; i < area.vertices.length; i++) {
                        let vid = area.vertices[i];
                        let coordsEqui = FloorplanManager.point2DToSphere(...vertices[vid], area.properties.scene);
                        let pair = this._getFloorpointPair(...coordsEqui, area.properties.scene);
                        this._vertices.push({
                            coord2d: vertices[vid],
                            floorSpher: pair[0],
                            ceilingSpher: pair[1],
                            floorCube: CubemapUtil.getBoxCoords(...pair[0]),
                            ceilingCube: CubemapUtil.getBoxCoords(...pair[1])
                        });
                    }

                    return;
                }
            }
        }
    }

    _findAreaIdLayer(){
        return Object.values(this._floorplan.layers).filter((layer) => Object.keys(layer.areas).includes(this._areaId))[0].id;
    }
    /**
     * Change current active layer
     * @param {object} floor
     */
    getLayerId() {
        return this._layerId;
    }

    selectLayer(id) {
        if(!id) id = this._findAreaIdLayer();
        this._layerId = id;
        this._floorplan.selectedLayer = id;
        this._floorplanPreview.selectedLayer = id;

        return id;
    }

    /**
     * Change current active layer to input layer
     * @param {object} floor
     */
    isLayerExists(floor) {
        let layerTo = this._floorplan.layers[floor.id];

        if(!layerTo) {
            return this._createLayer(floor.label, floor.id, floor.altitude);
        }

        return layerTo;
    }

    setFloor(floor) {
        const isNeedMove = floor.isNeedMove;
        if(this._layerId !== floor.id) {
            floor = this.isLayerExists(floor);
            if(isNeedMove) {
                this._moveArea(this._areaId, this._layerId, floor);
                this.setAngleForRotateCustomUpdate(floor.id);
            }
            this._layerId = floor.id;
            this._updateFloorplan(false);
        }
    }

    /**
     * Calculate altitude for new floor
     * based on altitude of previous floor and its highest wall.
     * @returns {number}
     */
    getNewFloorAltitude() {
        let altitude = 0,
            height = 0;
        let arr = Object.values(this._floorplan.layers);
        let altitudes = arr.map((layer) => layer.altitude);
        altitudes.sort(function(a,b) { return a - b;}).reverse();
        altitude = altitudes[0];

        let layer = arr.filter((layer) => layer.altitude == altitude)[0];

        if (!layer) return 0;

        altitude = layer.altitude || altitude;

        Object.keys(layer.lines).forEach(id => {
            let line = layer.lines[id];

            if (!line) return;

            let _height = Number(line.properties.height.length);
            height = height < _height ? _height : height;
        });

        if(!Object.keys(layer.lines).length) height = FloorplanManager.defaults.ceiling;

        return altitude + height;
    }

    /**
     * Public method to set area(scene) props
     * @param props
     */
    setCurrentSceneProps(props, update = false) {
        this._setCurrentSceneProps(cloneDeep(props), update);
    }

    /**
     * Update HotSpot spherical coordinates
     * @param id
     * @param spherCoords
     */
    updateHotspot(id, spherCoords) {
        if(!this.isDrawingFinished() || !this._hotspots[id]) return;
        this._hotspots[id].equiCoord = spherCoords.map(v => v * Pannellum.RADIAN);
        this._removeHoles();
        this._setHoles();

        this._updateFloorplan(false);
    }

    updatePreview() {
        this._updateFloorplans();
    }

    /**
     * Remove snapData property of vertex object
     * @param {number} index
     */
    removeVertexSnapData(index) {
        let vertex = this._vertices[index];
        if (vertex.snapData) {
            delete vertex.snapData;
        }
    }

    /**
     * Update vertex spherical and cubeBox coordinates for rendering
     * @param {number[]} spherCoords
     * @param {number} index
     * @param {boolean} adjustMode
     * @param {boolean} isFloor
     * @param {boolean} updateProps
     */
    updateVertexView(spherCoords, index, adjustMode, isFloor = true, updateProps = false) {
        if (!updateProps) {
            spherCoords = spherCoords.map(v => v * Pannellum.RADIAN);
        }
        let props = this._floorplan.layers[this._layerId].areas[this._areaId].properties.scene;
        let vertex = this._vertices[index];
        if (adjustMode) {
            if (isFloor) {
                vertex.floorSpher = [FloorplanManager.normalizeYaw(spherCoords[0]), spherCoords[1]];
                vertex.floorCube = CubemapUtil.getBoxCoords(...spherCoords);
            }
            else {
                vertex.ceilingSpher = [FloorplanManager.normalizeYaw(spherCoords[0]), spherCoords[1]];
                vertex.ceilingCube = CubemapUtil.getBoxCoords(...spherCoords);
            }
        }
        else {
            let pair = this._getFloorpointPair(...spherCoords, props);
            vertex.floorSpher = pair[0];
            vertex.ceilingSpher = pair[1];
            // vertex.coord2d = FloorplanManager.sphereTo2dNormalized(...pair[0], props);
            vertex.coord2d = FloorplanManager.sphereTo2dCorrected(...pair[0], props);
            if (this._vertices.length !== 1 && this._snapMode && !updateProps) {
                this._snapVertex(index);
            }
            else {
                vertex.floorCube = CubemapUtil.getBoxCoords(...pair[0]);
                vertex.ceilingCube = CubemapUtil.getBoxCoords(...pair[1]);
            }
        }
    }

    /**
     * Remove fromId layer and move all areas to toId layer
     * @param {string} fromId
     * @param {string} toId
     */
    removeLayerContent(id) {
        let fromLayer = this._floorplan.layers[id];
        Object.keys(fromLayer.areas).forEach(areaId => {
            if(this._areaId !== areaId){
                let area = fromLayer.areas[areaId];
                let linesId = [];
                let holesId = [];
                let verticesId = [];
                area.vertices.map((val) => {
                    fromLayer.vertices[val].lines.map((line) => {
                        fromLayer.lines[line].holes.map((hole) => holesId.push(hole));
                        if(linesId.indexOf(line) === -1) linesId.push(line);
                    });
                    verticesId.push(val);
                });
                holesId.map((val) =>  delete fromLayer.holes[val]);
                linesId.map((val) =>  delete fromLayer.lines[val]);
                verticesId.map((val) =>  delete fromLayer.vertices[val]);
                delete fromLayer.areas[areaId];
            }
        });
        this._updateFloorplan();
    }
    /**
     * is active area
     * @param {string} areaID
     * @param {string} toId
     */
    isActiveArea(areaID = this._areaId) {
        return areaID == this._areaId;
    }
    /**
     * is current area
     * @param {string} layerID
     * @param {string} areaID
     */
    isCurrentArea(layerID = this._layerId, areaID = this._areaId) {
        let fromLayer = this._floorplan.layers[layerID];
        let flag = false;
        Object.keys(fromLayer.areas).forEach(areaId => {
            if(areaID === areaId) flag = true;
        });

        return flag;
    }

    changeFloors(arr) {
        arr.forEach((layer) => {
            this._floorplan.layers[layer.id].altitude = layer.altitude;
            this._floorplanPreview.layers[layer.id].altitude = layer.altitude;
        })
    }

    removeHole(id) {
        let layer = this._floorplanPreview.layers[this._layerId];
        const holes = Object.values(layer.holes);

        this._hideHole(id);

        holes.forEach((line) => {
            if(line.holes && line.holes.includes(id)){
                const holeID = line.holes.filter((hole) => hole.id !== id);
                this._hideHole(holeID);
            }else{
                let hole = layer.holes[id];
                if(hole){
                    let { customHole } = layer.holes[id].properties;
                    if(customHole) this._removeHole(id);
                }
            }
        })
    }

    _removeHole(id){
        let layer = this._floorplanPreview.layers[this._layerId];
        delete layer.holes[id];
    }

    _hideHole(id){
        let layer = this._floorplanPreview.layers[this._layerId];
        const holes = Object.values(layer.holes);
        holes.forEach((hole) => {
            if(hole.id == id) hole.visible = false;
        })
    }

    /**
     * Remove fromId layer and areas
     * @param {string} fromId
     * @param {string} toId
     */

    removeLayerWithContent(fromId, toId) {
        let fromLayer = this._floorplan.layers[fromId];
        let toLayer = this._floorplan.layers[toId];

        if (!(fromLayer && toLayer)) return;

        Object.keys(fromLayer.areas).forEach(areaId => {
            let area = fromLayer.areas[areaId];
            let linesId = [];
            let holesId = [];
            let verticesId = [];
            area.vertices.map((val) => {
                fromLayer.vertices[val].lines.map((line) => {
                    fromLayer.lines[line].holes.map((hole) => holesId.push(hole));
                    if(linesId.indexOf(line) === -1) linesId.push(line);
                });
                verticesId.push(val);
            });
            area.vertices = [];
            holesId.map((val) =>  delete fromLayer.holes[val]);
            linesId.map((val) =>  delete fromLayer.lines[val]);
            verticesId.map((val) =>  delete fromLayer.vertices[val]);
            if(this._areaId === areaId) this._vertices = [];
        });

        Object.keys(fromLayer.areas).forEach(areaID => {
            toLayer.areas[areaID] = fromLayer.areas[areaID];
            delete fromLayer.areas[areaID];
        });

        this.selectLayer(toId);
        delete this._floorplan.layers[fromId];
        this._updateFloorplan(false);
    }

    // /**
    //  * Remove fromId layer and move all areas to toId layer
    //  * @param {string} fromId
    //  * @param {string} toId
    //  */
    // removeLayerWithoutContent(fromId, toId) {
    //     let idWithCurrentArea =  this.selectLayer();
    //     if(fromId !== idWithCurrentArea){
    //         toId = idWithCurrentArea;
    //     }else{
    //         this.selectLayer(toId);
    //     }
    //     let fromLayer = this._floorplan.layers[fromId];
    //     let toLayer = this._floorplan.layers[toId];
    //
    //     if (!(fromLayer && toLayer)) return;
    //
    //     Object.keys(fromLayer.areas).forEach(area => {
    //         let {id} = fromLayer.areas[area];
    //         this._moveArea(id, fromId, {id: toId});
    //     });
    //
    //     delete this._floorplan.layers[fromId];
    //     this._updateFloorplan();
    // }

    /**
     * Change current floor name
     * @param {string} name
     */
    setCurrentFloorAltitude(value, id) {
        let layer = this._floorplan.layers[id || this._layerId];
        layer.altitude = value;
    }

    /**
     * Update scene properties
     * @param properties, areaId
     */
    updateSceneProperties(data) {
        const {areaId} = data;
        const preview = this._floorplanPreview.layers[this._layerId];
        const area = preview.areas[areaId];
        delete data.areaId;

        area.properties = {...area.properties, ...data};
    }

    /**
     * Change font size for layer
     * @param {boolean} fontSize
     */
    _changeFontSizeLayer({fontSize}) {
        const preview = this._floorplanPreview.layers[this._layerId];
        preview.fontSize = fontSize;

        for(let areaID in preview.areas){
            preview.areas[areaID].properties.fontSize = fontSize;
        }
    }

    /**
     * Change current floor color
     * @param {string} color, {string}: areaId
     */
    updateCurrentColor(patternColor, areaId) {
        let preview = this._floorplanPreview.layers[this._layerId];
        preview.areas[areaId].properties.patternColor = patternColor;
    }

    /**
     * Change current floor name
     * @param {string} name, {string}: id
     */
    renameCurrentFloor(name, id) {
        let layer = this._floorplan.layers[id || this._layerId];
        layer.name = name;
    }

    /**
     * Change floor
     * @param {array} [width, height,...]
     */
    changeFloorplanParams(params) {
        for(let i in params){
            this._floorplan[i] = params[i];
            this._floorplanPreview[i] = params[i];
        }
    }

    destroy() {
        this._floorplan = null;
        this._floorplanPreview = null;
        this._changePreview = null;
    }

    setChangePreview(value) {
        this._changePreview = value;
    }

    isUpdatePreviewNeeded() {
        let res = this._changePreview;

        this._changePreview = false;

        return res;
    }
}
